import classNames from 'classnames'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import GridTable from '../common/GridTable/GridTable'
import SortButton from '../common/buttons/SortButton'
import OrganizationChips from '../common/OrganizationChips'
import CommunityChip from '../common/CommunityChip'
import CoachStatus from './CoachStatus'
import CoachCertification from './CoachCertification'
import { getNextSortValue } from '../../utility/tableSorting'

import AvatarPlaceholder from '../../assets/images/mock/default-profile-icon.png'
import classes from './CoachesTable.module.scss'

const CoachesTable = ({
  coachList, coachCount, hasMore, isLoading = false, isSortLoading = false, sortOption, onSort, onFetchMore, onRowClick, selectedCoachId,
}) => {
  const { t } = useTranslation()

  const firstColumn = {
    width: '280px',
    header: (
      <SortButton
        label={t('coaches.header.coachesCount', { count: coachCount })}
        active={sortOption.sortField === 'name' && sortOption.sortValue !== null}
        onClick={() => onSort('name', getNextSortValue(sortOption, 'name'))}
        loading={isSortLoading && sortOption.sortField === 'name'}
      />
    ),
    render: (coach) => (
      <>
        <img
          src={coach.avatar?.file_url || AvatarPlaceholder}
          alt={`${coach.first_name} ${coach.last_name}`}
          className={classNames(classes.coachAvatar, 'flex-shrink-0')}
        />
        <span className={classNames(classes.coachName, 'font-weight-bold ml-2')} title={`${coach.first_name} ${coach.last_name}`}>
          {coach.first_name} {coach.last_name}
        </span>
      </>
    ),
    skeleton: (
      <>
        <div data-skeleton='avatar' className='flex-shrink-0' />
        <div data-skeleton='name' className='ml-2' />
      </>
    ),
  }

  const columns = [
    { key: 'email', label: t('common.field.email'), gridWidth: 'minmax(220px, 1.5fr)', render: (coach) => coach.email },
    {
      key: 'community', label: t('coaches.common.community'), gridWidth: 'minmax(260px, 1.8fr)',
      render: (coach) => (coach.communities?.[0]
        ? <CommunityChip community={coach.communities[0]} />
        : <span className={classes.muted}>{t('coaches.table.freeCoachAccount')}</span>),
    },
    {
      key: 'organization', label: t('coaches.table.organization'), gridWidth: 'minmax(200px, 1.4fr)',
      render: (coach) => <OrganizationChips organizations={coach.organizations} emptyLabel={t('coaches.table.notAvailable')} />,
    },
    {
      key: 'certification', label: t('coaches.table.certification'), gridWidth: 'minmax(150px, 1fr)',
      render: (coach) => <CoachCertification coach={coach} />,
    },
    {
      key: 'phone', label: t('common.field.phoneNumber'), gridWidth: 'minmax(160px, 1fr)',
      render: (coach) => (coach.phone ? `${coach.phone_prefix ?? ''}${coach.phone}` : t('coaches.table.notAvailable')),
    },
    {
      key: 'created_at', label: t('coaches.table.dateCreated'), gridWidth: 'minmax(150px, 1fr)',
      render: (coach) => (coach.created_at ? moment(coach.created_at).format('MMM D, YYYY') : t('coaches.table.notAvailable')),
    },
    {
      key: 'last_sign_in', label: t('coaches.table.lastSignIn'), gridWidth: 'minmax(150px, 1fr)',
      render: (coach) => (coach.current_sign_in_at ? moment(coach.current_sign_in_at).format('MMM D, YYYY') : t('coaches.table.never')),
    },
    { key: 'status', label: t('common.field.status'), gridWidth: 'minmax(160px, 1fr)', render: (coach) => <CoachStatus status={coach.status} /> },
  ]

  return (
    <GridTable
      id='coachesTable'
      className={classes.table}
      firstColumn={firstColumn}
      columns={columns.map(column => ({
        key: column.key,
        gridWidth: column.gridWidth,
        header: (
          <SortButton
            label={column.label}
            active={sortOption.sortField === column.key && sortOption.sortValue !== null}
            onClick={() => onSort(column.key, getNextSortValue(sortOption, column.key))}
            loading={isSortLoading && sortOption.sortField === column.key}
          />
        ),
        render: column.render,
        skeleton: <div data-skeleton='value' />,
      }))}
      rows={coachList}
      rowKey={(coach) => coach.id}
      hasMore={hasMore}
      onFetchMore={onFetchMore}
      hideLoader
      onRowClick={onRowClick}
      selectedRowKey={selectedCoachId}
      skeletonRowsCount={hasMore && isLoading ? 3 : 0}
    />
  )
}

CoachesTable.propTypes = {
  coachList: PropTypes.arrayOf(PropTypes.object).isRequired,
  coachCount: PropTypes.number.isRequired,
  hasMore: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool,
  isSortLoading: PropTypes.bool,
  sortOption: PropTypes.shape({ sortField: PropTypes.string, sortValue: PropTypes.string }).isRequired,
  onSort: PropTypes.func.isRequired,
  onFetchMore: PropTypes.func.isRequired,
  onRowClick: PropTypes.func.isRequired,
  selectedCoachId: PropTypes.number,
}

export default CoachesTable
